import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { fetchCategory } from "@/lib/api";
import { resolveImage, imgSrc } from "@/lib/images";
import KnowledgeCard from "@/components/KnowledgeCard";
import JourneyStrip from "@/components/JourneyStrip";
import RichText from "@/components/RichText";
import Seo from "@/components/Seo";
import { MandalaMotif } from "@/components/MotifBackground";
import { ChevronRight } from "lucide-react";

const GROUPS = [
  ["mantra", "मंत्र", "Mantras"],
  ["sukta", "सूक्त", "Suktas"],
  ["stotra", "स्तोत्र", "Stotras"],
  ["grantha", "ग्रंथ", "Scriptures"],
  ["puja_vidhi", "पूजा विधि", "Pooja Vidhi"],
  ["katha", "कथा", "Kathas"],
  ["festival", "पर्व", "Festivals"],
];

const onImgError = (e) => { e.currentTarget.style.opacity = "0"; };
const catImage = (c) => resolveImage({ image: c.image || c.image_url, deity: c.name, name: `${c.deva_name || ""} ${c.name || ""}` });

export default function CategoryLanding() {
  const { slug } = useParams();
  const location = useLocation();
  const { pick } = useLang();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setC(null); setNotFound(false);
    fetchCategory(slug).then(setC).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Category not found.</T></div>;
  if (!c) return <OmSpinner />;

  const content = c.content || {};
  const img = catImage(c);
  const resourceCount = GROUPS.reduce((n, [t]) => n + (content[t]?.length || 0), 0);
  const sections = GROUPS.filter(([t]) => content[t]?.length).map(([t, deva, en]) => ({ id: `sec-${t}`, deva, en }));

  return (
    <article className="relative pb-8" data-testid="category-detail">
      <Seo path="/categories" title={`${c.name} — Category`} description={c.description} image={img} />

      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -left-24 top-1/2 hidden h-[420px] w-[420px] -translate-y-1/2 text-[var(--gold)] opacity-[0.06] md:block" />
        <div className="relative z-[2] mx-auto grid max-w-7xl gap-10 px-5 py-12 md:grid-cols-[1.05fr_0.95fr] md:items-center md:px-8 md:py-16">
          <div>
            <nav className="flex items-center gap-1.5 text-xs text-ink-3">
              <Link to="/" className="hover:text-saffron"><T>Dharmartha</T></Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/categories" className="hover:text-saffron">{pick("श्रेणियाँ", "Categories")}</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-ink-2">{c.name}</span>
            </nav>
            <p className="mt-6 overline-label text-gold">{pick("श्रेणी", "Category")}</p>
            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-2 font-deva text-5xl leading-tight text-ink deva-lh md:text-7xl" data-testid="category-title">{c.deva_name || c.name}</motion.h1>
            {c.deva_name && <p className="mt-2 font-serif-latin text-2xl italic text-ink-2">{c.name}</p>}
            <p className="mt-5 text-sm text-ink-3">{resourceCount} {pick("संसाधन", resourceCount === 1 ? "resource" : "resources")}</p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="relative mx-auto w-full max-w-sm md:max-w-none">
            <div className="relative overflow-hidden border border-[var(--gold-muted)] bg-[var(--surface-alt)] shadow-xl" style={{ aspectRatio: "4/5" }}>
              <img src={imgSrc(img, 700)} alt={c.name} loading="eager" decoding="async" onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
            </div>
            <span className="absolute -right-3 -top-3 hidden h-16 w-16 border border-[var(--gold-muted)] md:block" aria-hidden="true" />
          </motion.div>
        </div>

        {sections.length > 1 && (
          <div className="relative z-[2] mx-auto max-w-7xl px-5 pb-8 md:px-8">
            <div className="flex flex-wrap gap-2">
              {sections.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="inline-flex items-center gap-2 border border-line bg-surface/70 px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  <span className="font-deva text-saffron/70">{s.deva[0]}</span> {pick(s.deva, s.en)}
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {(c.description || c.description_hi) && (
          <section className="mt-12 scroll-mt-24" data-testid="category-about">
            <div className="mb-6 flex items-baseline gap-3">
              <span className="h-px w-8 bg-[var(--gold)]" />
              <h2 className="font-deva text-2xl text-saffron deva-lh">परिचय</h2>
              <span className="overline-label text-ink-3">{pick("परिचय", "About")}</span>
            </div>
            <RichText value={pick(c.description_hi, c.description)} className="max-w-[72ch] text-[15px] leading-[1.9] text-ink-2 md:text-base" />
          </section>
        )}

        {resourceCount === 0 && <p className="mt-16 text-center text-ink-3">{pick("संबंधित सामग्री शीघ्र जोड़ी जा रही है।", "Associated content is being added.")}</p>}

        {GROUPS.map(([type, deva, en]) =>
          content[type]?.length ? (
            <section key={type} id={`sec-${type}`} className="mt-14 scroll-mt-24" data-testid={`category-group-${type}`}>
              <div className="mb-6 flex items-baseline gap-3">
                <span className="h-px w-8 bg-[var(--gold)]" />
                <h2 className="font-deva text-2xl text-saffron deva-lh">{deva}</h2>
                <span className="overline-label text-ink-3">{en}</span>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {content[type].map((it) => <KnowledgeCard key={it.slug} item={it} showImage />)}
              </div>
            </section>
          ) : null
        )}
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <JourneyStrip links={["mantras", "stotras", "rituals", "festivals", "deities"]} />
      </div>
    </article>
  );
}
